import React, { lazy, Suspense } from "react";
import { PageSkeleton } from "./components/SectionSkeleton";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Feed from "./pages/Feed";
const PrayerGroups = lazy(() => import("./pages/PrayerGroups"));
const Events = lazy(() => import("./pages/Events"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Members = lazy(() => import("./pages/Members"));
const Profile = lazy(() => import("./pages/Profile"));
const Admin = lazy(() => import("./pages/Admin"));
const Give = lazy(() => import("./pages/Give"));
const Services = lazy(() => import("./pages/Services"));
const HelpGuide = lazy(() => import("./pages/HelpGuide"));
const MusicLibrary = lazy(() => import("./pages/MusicLibrary"));
const GrowthGroups = lazy(() => import("./pages/GrowthGroups"));
const ChurchCalendar = lazy(() => import("./pages/ChurchCalendar"));
const ProjectorMode = lazy(() => import("./pages/ProjectorMode"));
const Directory = lazy(() => import("./pages/Directory"));
const OwnerDashboard = lazy(() => import("./pages/OwnerDashboard"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const OrderOfService = lazy(() => import("./pages/OrderOfService"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-teal"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-church-teal"></div>
      </div>
    );
  }
  
  if (!isAdmin) {
    return <Navigate to="/feed" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/owner" element={<OwnerDashboard />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/give" element={<Give />} />
      <Route path="/give/success" element={<Give />} />
      <Route path="/feed" element={
        <ProtectedRoute>
          <Feed />
        </ProtectedRoute>
      } />
      <Route path="/prayer-groups" element={
        <ProtectedRoute>
          <PrayerGroups />
        </ProtectedRoute>
      } />
      <Route path="/events" element={
        <ProtectedRoute>
          <Events />
        </ProtectedRoute>
      } />
      <Route path="/announcements" element={
        <ProtectedRoute>
          <Announcements />
        </ProtectedRoute>
      } />
      <Route path="/members" element={
        <ProtectedRoute>
          <Members />
        </ProtectedRoute>
      } />
      <Route path="/roster" element={
        <ProtectedRoute>
          <Services />
        </ProtectedRoute>
      } />
      <Route path="/order-of-service" element={
        <ProtectedRoute>
          <OrderOfService />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <HelpGuide />
        </ProtectedRoute>
      } />
      <Route path="/music" element={
        <ProtectedRoute>
          <MusicLibrary />
        </ProtectedRoute>
      } />
      <Route path="/growth-groups" element={
        <ProtectedRoute>
          <GrowthGroups />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <ChurchCalendar />
        </ProtectedRoute>
      } />
      <Route path="/present" element={
        <ProtectedRoute>
          <ProjectorMode />
        </ProtectedRoute>
      } />
      <Route path="/present/:songId" element={
        <ProtectedRoute>
          <ProjectorMode />
        </ProtectedRoute>
      } />
      <Route path="/present/setlist/:setlistId" element={
        <ProtectedRoute>
          <ProjectorMode />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/directory" element={
        <ProtectedRoute>
          <Directory />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute>
          <Admin />
        </AdminRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageSkeleton />}>
            <AppRoutes />
          </Suspense>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
